import {
  DEPOSIT_MULTIPLIERS,
  TRUSTED_BID_LIMIT_MAX,
  TRUSTED_BID_LIMIT_MIN,
  type BiddingModeKind,
  type ClientSeesLabel,
} from '@carsfinders/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';

import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { apiErrorCode, fetchUserById } from '../api';
import {
  LEGACY_DRAFT,
  belowCommittedWarning,
  isDraftDirty,
  previewOf,
  clampBidLimit,
  isDraftValid,
  savedDraftOf,
  useBiddingMode,
  type BiddingDraft,
  type BiddingPreview,
} from '../hooks/use-bidding-mode';
import {
  type BiddingModeChangeResult,
  type BiddingModeSnapshot,
  type WireBiddingJournalEntry,
} from '../types';
import { clientName, cn, formatAmount } from '../utils';
import { ConfirmationModal } from './confirmation-modal';

const MODES: readonly BiddingModeKind[] = ['deposit', 'trusted'];

/** CHF amounts in the «Клієнт бачить» caption — every other param is a plain count. */
const CHF_PARAMS = new Set(['freeChf', 'capChf']);

/**
 * «Клієнт бачить: …» in the operator's language. The API (or the preview)
 * hands over an i18n key and raw numbers; money is formatted here so the
 * caption reads `CHF 12 500`, and every param is resolved to a literal before
 * `t()` so a zero never leaks a raw `{token}`.
 */
function clientSeesText(label: ClientSeesLabel): string {
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(label.params)) {
    params[key] = CHF_PARAMS.has(key) ? formatAmount(value) : String(value);
  }
  return t(label.key, params);
}

function chf(value: number): string {
  return t('clients.biddingMode.chf', { amount: formatAmount(value) });
}

/** `16.08` — the artboard's «змінено» date. */
function dayMonth(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('uk-UA', {
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

/**
 * Arrow keys move the checked option of a `role="radiogroup"` (WAI-ARIA radio
 * pattern): the group is one tab stop, and the focus follows the selection.
 */
function radioKeyDown<T>(
  event: KeyboardEvent<HTMLElement>,
  options: readonly T[],
  current: T,
  pick: (value: T) => void,
  focus: (index: number) => void,
): void {
  const forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
  const backward = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
  if (!forward && !backward) return;
  event.preventDefault();
  const index = options.indexOf(current);
  const next = (index + (forward ? 1 : -1) + options.length) % options.length;
  pick(options[next] as T);
  focus(next);
}

/**
 * «Режим ставок» — the aside of artboard «Admin · Клієнт» (admin deals &
 * deposits, phase 9, SC-14).
 *
 * The operator picks Депозит (×5/×10/×15 of the CHF deposit is the ceiling on
 * committed CHF) or Довірений (N simultaneous bids). Every figure is the API's
 * (`GET …/bidding-mode`); an unsaved choice is previewed with the same pure
 * contracts functions the API enforces with. Save is live only while the draft
 * differs from what is stored, and writes one journal row.
 */
export function BiddingModeCard({ userId }: { userId: string }) {
  const { snapshotQuery, journalQuery, saveMutation } = useBiddingMode(userId);
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      data-testid="bidding-mode-card"
      className="flex flex-col gap-[18px] rounded-cf-lg border border-cf-line bg-cf-surface p-4 leading-[normal] lg:px-6 lg:py-[22px]"
    >
      {snapshotQuery.isLoading ? (
        <>
          <CardHeading id={headingId} />
          <p className="m-0 text-[13px] text-cf-ink-subtle" role="status">
            {t('clients.biddingMode.loading')}
          </p>
        </>
      ) : snapshotQuery.isError || !snapshotQuery.data ? (
        <>
          <CardHeading id={headingId} />
          <div role="alert" data-testid="bidding-mode-load-error" className="flex flex-col gap-2">
            <p className="m-0 text-[13px] text-cf-danger">{t('clients.biddingMode.loadFailed')}</p>
            <button
              type="button"
              onClick={() => void snapshotQuery.refetch()}
              className="h-9 w-fit rounded-cf-md border border-cf-line bg-cf-surface px-3 text-[13px] font-semibold hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink"
            >
              {t('clients.biddingMode.retry')}
            </button>
          </div>
        </>
      ) : (
        <BiddingModeEditor
          headingId={headingId}
          snapshot={snapshotQuery.data}
          lastChange={journalQuery.data?.[0] ?? null}
          saving={saveMutation.isPending}
          onSave={(draft) => saveMutation.mutateAsync(draft)}
        />
      )}
    </section>
  );
}

function CardHeading({ id, aside }: { id: string; aside?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <h2 id={id} className="m-0 text-[18px] font-semibold tracking-[-0.02em]">
        {t('clients.biddingMode.title')}
      </h2>
      {aside}
    </div>
  );
}

interface EditorProps {
  headingId: string;
  snapshot: BiddingModeSnapshot;
  lastChange: WireBiddingJournalEntry | null;
  saving: boolean;
  onSave: (draft: BiddingDraft) => Promise<BiddingModeChangeResult>;
}

function BiddingModeEditor({ headingId, snapshot, lastChange, saving, onSave }: EditorProps) {
  const saved = savedDraftOf(snapshot);
  // `null` = the operator has not touched the card: the draft IS the saved
  // mode, and a refetch that changes it re-renders without clobbering edits.
  const [override, setOverride] = useState<BiddingDraft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const modeRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const multRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const dirtyTextId = useId();
  const hintId = useId();

  const draft = override ?? saved ?? LEGACY_DRAFT;
  const dirty = isDraftDirty(saved, draft);
  const canSave = dirty && isDraftValid(draft);
  const preview = previewOf(snapshot, draft);
  const warning = belowCommittedWarning(preview, dirty);
  // Owner decision (d), 2026-09-28: a deposit left on a client switched to
  // trusted stays as a prepayment for the next invoice (no auto-refund).
  const keptDepositChf =
    draft.mode === 'trusted' && saved?.mode !== 'trusted' && snapshot.depositChf > 0
      ? snapshot.depositChf
      : 0;

  const edit = (patch: Partial<BiddingDraft>) => {
    setSaveError(null);
    setOverride({ ...draft, ...patch });
  };

  // Switching to trusted clamps a stored limit the PUT would refuse (0, 120…).
  const pickMode = (mode: BiddingModeKind) =>
    edit(mode === 'trusted' ? { mode, bidLimit: clampBidLimit(draft.bidLimit) } : { mode });

  const confirmLines = [
    snapshot.legacy ? t('clients.biddingMode.confirm.legacy') : null,
    keptDepositChf > 0
      ? t('clients.biddingMode.confirm.depositKept', {
          amount: formatAmount(keptDepositChf),
        })
      : null,
    warning ? t(`clients.biddingMode.warning.${warning}`) : null,
  ].filter((line): line is string => line !== null);

  const save = async () => {
    setSaveError(null);
    try {
      const result = await onSave(draft);
      setConfirming(false);
      setOverride(null);
      const kept = result.depositKeptAsPrepaymentChf;
      toast.success(
        kept > 0
          ? t('clients.biddingMode.savedKept', { amount: formatAmount(kept) })
          : t('clients.biddingMode.saved'),
      );
    } catch (error) {
      setConfirming(false);
      // The draft is kept so the operator can adjust and retry.
      const code = apiErrorCode(error);
      setSaveError(
        code === 'NO_CHANGE'
          ? t('clients.biddingMode.errors.noChange')
          : code
            ? t('clients.biddingMode.errors.saveFailedCode', { code })
            : t('clients.biddingMode.errors.saveFailed'),
      );
    }
  };

  const onSaveClick = () => {
    if (!canSave || saving) return;
    if (confirmLines.length > 0) setConfirming(true);
    else void save();
  };

  const statusText = snapshot.legacy ? t('clients.biddingMode.legacy') : null;

  return (
    <>
      <CardHeading
        id={headingId}
        aside={
          <span
            data-testid="bidding-mode-last-change"
            className="font-mono text-[12px] whitespace-nowrap text-cf-ink-subtle"
          >
            {statusText ?? <LastChange snapshot={snapshot} entry={lastChange} />}
          </span>
        }
      />

      {(lastChange?.depositKeptAsPrepaymentChf ?? 0) > 0 && (
        <p
          data-testid="bidding-mode-last-kept"
          className="m-0 -mt-2.5 text-[12px] leading-[1.5] text-cf-ink-subtle"
        >
          {t('clients.biddingMode.lastKept', {
            amount: formatAmount(lastChange?.depositKeptAsPrepaymentChf ?? 0),
          })}
        </p>
      )}

      <div
        role="radiogroup"
        aria-labelledby={headingId}
        aria-describedby={hintId}
        className="grid grid-cols-2 gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]"
      >
        {MODES.map((mode, index) => {
          const on = draft.mode === mode;
          return (
            <button
              key={mode}
              ref={(node) => {
                modeRefs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              data-testid={`bidding-mode-${mode}`}
              onClick={() => pickMode(mode)}
              onKeyDown={(event) =>
                radioKeyDown(event, MODES, draft.mode, pickMode, (i) =>
                  modeRefs.current[i]?.focus(),
                )
              }
              className={cn(
                'h-10 rounded-cf-md text-[14px] font-semibold text-cf-ink focus-visible:outline-2 focus-visible:outline-cf-ink',
                on ? 'bg-cf-surface shadow-[0_1px_2px_rgba(0,0,0,0.08)]' : 'bg-transparent',
              )}
            >
              {t(`clients.biddingMode.modes.${mode}`)}
            </button>
          );
        })}
      </div>
      <p id={hintId} className="m-0 -mt-1.5 text-[12px] leading-[1.5] text-cf-ink-subtle">
        {t(`clients.biddingMode.hint.${draft.mode}`)}
      </p>

      {draft.mode === 'deposit' ? (
        <MultiplierPicker
          value={draft.multiplier}
          refs={multRefs}
          onPick={(multiplier) => edit({ multiplier })}
        />
      ) : (
        <BidLimitStepper
          value={draft.bidLimit}
          active={snapshot.activeBidCount}
          onChange={(bidLimit) => edit({ bidLimit })}
        />
      )}

      <Facts preview={preview} depositChf={snapshot.depositChf} />

      {warning && (
        <p
          role="alert"
          data-testid="bidding-mode-warning"
          className="m-0 flex gap-2 rounded-cf-md bg-cf-danger-tint px-3 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
        >
          {t(`clients.biddingMode.warning.${warning}`)}
        </p>
      )}

      <ClientSees preview={preview} />

      {saveError && (
        <p
          role="alert"
          data-testid="bidding-mode-save-error"
          className="m-0 rounded-cf-md bg-cf-danger-tint px-3 py-2.5 text-[13px] leading-[1.5] text-cf-danger"
        >
          {saveError}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-cf-line-soft pt-4">
        <span id={dirtyTextId} aria-live="polite" className="text-[12px] text-cf-ink-subtle">
          {snapshot.legacy
            ? t('clients.biddingMode.legacyDirty')
            : dirty
              ? t('clients.biddingMode.dirty')
              : t('clients.biddingMode.clean')}
        </span>
        <button
          type="button"
          data-testid="bidding-mode-save"
          onClick={onSaveClick}
          disabled={!canSave || saving}
          aria-describedby={dirtyTextId}
          className="h-10 shrink-0 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-cf-brand"
        >
          {saving ? t('clients.biddingMode.saving') : t('clients.biddingMode.save')}
        </button>
      </div>

      <ConfirmationModal
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => void save()}
        title={t('clients.biddingMode.confirm.title')}
        message={confirmLines.join(' ')}
        confirmText={t('clients.biddingMode.save')}
        isLoading={saving}
      />
    </>
  );
}

/** «змінено 16.08 · Андрій» — newest journal row, else the stored mode's own stamp. */
function LastChange({
  snapshot,
  entry,
}: {
  snapshot: BiddingModeSnapshot;
  entry: WireBiddingJournalEntry | null;
}) {
  const changedAt = entry?.changedAt ?? snapshot.bidding?.changedAt ?? null;
  const changedBy = entry?.changedBy ?? snapshot.bidding?.changedBy ?? '';
  // Same key the client page uses for a user, so an admin already loaded is free.
  const whoQuery = useQuery({
    queryKey: ['user', changedBy],
    queryFn: () => fetchUserById(changedBy),
    enabled: changedBy.length > 0,
    retry: false,
  });
  if (!changedAt) return null;
  const who = whoQuery.data ? clientName(whoQuery.data) : '';
  return (
    <>
      {who
        ? t('clients.biddingMode.changed', { date: dayMonth(changedAt), who })
        : t('clients.biddingMode.changedNoWho', { date: dayMonth(changedAt) })}
    </>
  );
}

function MultiplierPicker({
  value,
  refs,
  onPick,
}: {
  value: BiddingDraft['multiplier'];
  refs: MutableRefObject<Array<HTMLButtonElement | null>>;
  onPick: (value: BiddingDraft['multiplier']) => void;
}) {
  const labelId = useId();
  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-[13px] text-cf-ink-muted">
        {t('clients.biddingMode.multiplierLabel')}
      </span>
      <div role="radiogroup" aria-labelledby={labelId} className="grid grid-cols-3 gap-1.5">
        {DEPOSIT_MULTIPLIERS.map((multiplier, index) => {
          const on = value === multiplier;
          return (
            <button
              key={multiplier}
              ref={(node) => {
                refs.current[index] = node;
              }}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on ? 0 : -1}
              data-testid={`bidding-mode-multiplier-${multiplier}`}
              onClick={() => onPick(multiplier)}
              onKeyDown={(event) =>
                radioKeyDown(event, DEPOSIT_MULTIPLIERS, value, onPick, (i) =>
                  refs.current[i]?.focus(),
                )
              }
              className={cn(
                'h-11 rounded-cf-md border-[1.5px] font-mono text-[16px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-ink',
                on
                  ? 'border-cf-brand bg-cf-brand text-white'
                  : 'border-cf-line bg-cf-surface text-cf-ink hover:border-cf-ink',
              )}
            >
              {t('clients.biddingMode.multiplierOption', { multiplier })}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BidLimitStepper({
  value,
  active,
  onChange,
}: {
  value: number;
  active: number;
  onChange: (value: number) => void;
}) {
  const labelId = useId();
  const atMin = value <= TRUSTED_BID_LIMIT_MIN;
  const atMax = value >= TRUSTED_BID_LIMIT_MAX;
  const stepClass =
    'w-11 border-0 bg-cf-fill text-[18px] hover:bg-cf-line focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-cf-ink disabled:cursor-not-allowed disabled:opacity-40';
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div id={labelId} className="text-[13px] text-cf-ink-muted">
          {t('clients.biddingMode.bidLimitLabel')}
        </div>
        <div className="text-[12px] text-cf-ink-subtle">
          {t('clients.biddingMode.activeNow', { count: String(active) })}
        </div>
      </div>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex h-11 items-stretch overflow-hidden rounded-cf-md border border-cf-line"
      >
        <button
          type="button"
          aria-label={t('clients.biddingMode.decrease')}
          data-testid="bidding-mode-decrease"
          disabled={atMin}
          onClick={() => onChange(Math.max(TRUSTED_BID_LIMIT_MIN, value - 1))}
          className={stepClass}
        >
          −
        </button>
        <output
          aria-live="polite"
          data-testid="bidding-mode-bid-limit"
          className="flex w-14 items-center justify-center font-mono text-[18px]"
        >
          {value}
        </output>
        <button
          type="button"
          aria-label={t('clients.biddingMode.increase')}
          data-testid="bidding-mode-increase"
          disabled={atMax}
          onClick={() => onChange(Math.min(TRUSTED_BID_LIMIT_MAX, value + 1))}
          className={stepClass}
        >
          +
        </button>
      </div>
    </div>
  );
}

function Facts({ preview, depositChf }: { preview: BiddingPreview; depositChf: number }) {
  const bp = preview.buyingPower;
  let facts: Array<{
    key: string;
    label: string;
    value: string;
    tone: 'ink' | 'ok' | 'danger';
  }>;
  let barPct: number;
  let blocked: boolean;
  let note: string;

  if (bp.mode === 'deposit') {
    const overBy = bp.committedChf - bp.capChf;
    blocked = bp.freeChf <= 0;
    facts = [
      {
        key: 'deposit',
        label: t('clients.biddingMode.facts.deposit'),
        value: chf(bp.depositChf),
        tone: 'ink',
      },
      {
        key: 'cap',
        label: t('clients.biddingMode.facts.cap'),
        value: chf(bp.capChf),
        tone: 'ink',
      },
      {
        key: 'free',
        label: t('clients.biddingMode.facts.free'),
        value: bp.overCeiling
          ? t('clients.biddingMode.overBy', { amount: formatAmount(overBy) })
          : chf(bp.freeChf),
        tone: blocked ? 'danger' : 'ok',
      },
    ];
    barPct = bp.capChf > 0 ? Math.min(100, Math.round((bp.committedChf / bp.capChf) * 100)) : 100;
    note =
      bp.depositChf <= 0
        ? t('clients.biddingMode.note.noDeposit')
        : bp.overCeiling
          ? t('clients.biddingMode.note.over', { amount: formatAmount(overBy) })
          : blocked
            ? t('clients.biddingMode.note.full')
            : t('clients.biddingMode.note.canBid');
  } else {
    blocked = bp.remainingBids <= 0;
    facts = [
      {
        key: 'limit',
        label: t('clients.biddingMode.facts.limit'),
        value: String(bp.bidLimit),
        tone: 'ink',
      },
      {
        key: 'active',
        label: t('clients.biddingMode.facts.active'),
        value: String(bp.activeBidCount),
        tone: 'ink',
      },
      {
        key: 'free',
        label: t('clients.biddingMode.facts.free'),
        value: String(bp.remainingBids),
        tone: blocked ? 'danger' : 'ok',
      },
    ];
    barPct =
      bp.bidLimit > 0 ? Math.min(100, Math.round((bp.activeBidCount / bp.bidLimit) * 100)) : 100;
    // «Зайнято» counts won-but-unpaid lots too (API bid-slots.ts) — say so.
    note = [
      blocked
        ? t('clients.biddingMode.note.limitReached')
        : t('clients.biddingMode.note.remaining', {
            count: String(bp.remainingBids),
          }),
      t('clients.biddingMode.note.slotsInclude'),
      !blocked && depositChf > 0
        ? t('clients.biddingMode.note.depositKept', {
            amount: formatAmount(depositChf),
          })
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  return (
    <div className="flex flex-col gap-2.5">
      {/* BUG-41: a money figure is never elided. The facts stack on a narrow
          viewport and wrap inside a narrow column (the client page's sidebar at
          1280 px) instead of «CHF 10 …». */}
      <dl className="m-0 grid grid-cols-1 border-y border-cf-line-soft sm:grid-cols-3">
        {facts.map((fact, index) => (
          <div
            key={fact.key}
            data-testid={`bidding-mode-fact-${fact.key}`}
            className={cn(
              'min-w-0 py-3 pr-3',
              index > 0
                ? 'border-t border-cf-line-soft pl-0 sm:border-t-0 sm:border-l sm:pl-3.5'
                : 'pl-0',
            )}
          >
            <dt className="font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase">
              {fact.label}
            </dt>
            <dd
              className={cn(
                'm-0 mt-1 font-mono text-[14px] font-medium break-words xl:text-[16px]',
                fact.tone === 'ok'
                  ? 'text-cf-success'
                  : fact.tone === 'danger'
                    ? 'text-cf-danger'
                    : 'text-cf-ink',
              )}
            >
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
      <div
        role="meter"
        aria-label={t('clients.biddingMode.usage')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={barPct}
        className="h-1.5 overflow-hidden rounded-[3px] bg-cf-fill"
      >
        <div
          data-testid="bidding-mode-usage"
          className={cn('h-full', blocked ? 'bg-cf-danger' : 'bg-cf-ink-faint')}
          style={{ width: `${barPct}%` }}
        />
      </div>
      <p
        data-testid="bidding-mode-note"
        className={cn(
          'm-0 text-[12px] leading-[1.5]',
          bp.mode === 'deposit'
            ? bp.overCeiling
              ? 'text-cf-danger'
              : 'text-cf-ink-subtle'
            : blocked
              ? 'text-cf-danger'
              : 'text-cf-ink-subtle',
        )}
      >
        {note}
      </p>
    </div>
  );
}

function ClientSees({ preview }: { preview: BiddingPreview }) {
  const bp = preview.buyingPower;
  const canBid = bp.mode === 'deposit' ? bp.freeChf > 0 && bp.depositChf > 0 : bp.remainingBids > 0;
  return (
    <div className="flex items-center gap-2.5 rounded-cf-md bg-cf-ground px-3 py-2.5 text-[13px]">
      <span className="text-cf-ink-subtle">{t('clients.biddingMode.clientSeesLabel')}</span>
      <span data-testid="bidding-mode-client-sees" className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={cn('h-2 w-2 rounded-[2px]', canBid ? 'bg-cf-success' : 'bg-cf-danger')}
        />
        {clientSeesText(preview.clientSees)}
      </span>
    </div>
  );
}
